import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import { Button, Skeleton } from "@mui/material";

import { findAcaoQualificacao } from "../../../API/apiAcoes";
import { AcaoQualificacao } from "../../../types/acao";

import styles from "./InscricaoAcao.module.css";
import styles_button from "../../../styles/Button.module.css";

const TERMO_COMPROMISSO =
  "Declaro para os devidos fins que tenho disponibilidade para participar integralmente dessa ação de qualificação. Declaro, ainda, estar ciente do tempo de duração da qualificação e que irei acompanhar o e-mail informado na minha ficha de inscrição e a sua caixa de spam, pois sei que as informações sobre a ação poderão ser enviadas para ele. Comprometo-me com a vaga disponibilizada para a minha participação e com a veracidade das informações preenchidas no ato da minha inscrição.";

const ALERTA_TERMO =
  "Atenção! Muitas outras pessoas gostariam de ter a oportunidade, por isso pedimos que você a aproveite ao máximo, não desperdice a vaga que conquistou para essa qualificação gratuita e realize todas as atividades propostas para sua aprovação.";

const OrientacaoAcao = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const acaoId = searchParams.get("id");
  const [loading, setLoading] = useState<boolean>(true);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [acao, setAcao] = useState<AcaoQualificacao | null>(null);

  const apiPath = useMemo(
    () => process.env.REACT_APP_API_URL?.replace("/api", "") || "",
    [],
  );

  useEffect(() => {
    const carregarAcao = async () => {
      try {
        if (!acaoId) {
          setMsgError("Acao nao encontrada.");
          setLoading(false);
          return;
        }

        const resultado = await findAcaoQualificacao(acaoId);
        setAcao(resultado);
      } catch (error) {
        setMsgError("Nao foi possivel carregar as orientacoes da acao.");
      } finally {
        setLoading(false);
      }
    };

    carregarAcao();
  }, [acaoId]);

  const arquivoUrl = useMemo(() => {
    if (!acao?.arquivo_path) {
      return "";
    }

    if (
      acao.arquivo_path.startsWith("http://") ||
      acao.arquivo_path.startsWith("https://")
    ) {
      return acao.arquivo_path;
    }

    return `${apiPath}${acao.arquivo_path}`;
  }, [acao?.arquivo_path, apiPath]);

  const isPdf = useMemo(
    () => arquivoUrl.toLowerCase().split("?")[0].endsWith(".pdf"),
    [arquivoUrl],
  );

  if (loading) {
    return (
      <>
        <div className={styles.header} aria-busy="true">
          <Skeleton variant="text" width={340} height={44} />
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="56%" height={28} />
        </div>
        <div className={styles.acaoCard}>
          <Skeleton variant="text" width={280} height={30} />
          <Skeleton variant="rounded" width="100%" height={450} />
          <Skeleton variant="text" width={230} height={30} />
          <Skeleton variant="rounded" width="100%" height={150} />
          <div className={styles.actionsRight}>
            <Skeleton variant="rounded" width={96} height={34} />
            <Skeleton variant="rounded" width={300} height={34} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <h1 className={`${styles.title} ${styles.textLeft}`}>
          Orientações e termo de compromisso
        </h1>
        <p className={`${styles.subtitle} ${styles.textLeft}`}>
          Abaixo estão algumas informações complementares sobre a ação e o termo
          de compromisso da atividade. Leia com atenção e prossiga para a
          selecionar sua turma.
        </p>
      </div>

      <div className={styles.acaoCard}>
        {msgError && (
          <div className={styles.errorWrapper}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        {!msgError && (
          <>
            <h2 className={`${styles.sectionTitle} ${styles.textLeft}`}>
              Informações complementares da ação
            </h2>
            <div className={styles.fileWrapper}>
              {!arquivoUrl ? (
                <div className={styles.fileEmpty}>
                  Ação sem arquivo complementar.
                </div>
              ) : isPdf ? (
                <iframe
                  src={arquivoUrl}
                  title="Arquivo complementar da ação"
                  className={styles.pdfViewer}
                />
              ) : (
                <img
                  src={arquivoUrl}
                  alt="Informações complementares da ação"
                  className={styles.imageViewer}
                />
              )}
            </div>

            <h2 className={`${styles.sectionTitle} ${styles.textLeft}`}>
              Termo de Compromisso
            </h2>
            <div className={styles.termoBox}>
              <p>{TERMO_COMPROMISSO}</p>
              <p className={styles.termoAlerta}>{ALERTA_TERMO}</p>
            </div>
          </>
        )}

        <div className={styles.actionsRight}>
          <Button
            variant="contained"
            className={styles_button.buttonSquareDanger}
            onClick={() => navigate("/realizar-inscricao")}
          >
            Voltar
          </Button>

          <Button
            variant="contained"
            className={`${styles.buttonConcordo} ${styles_button.buttonSquareSuccess}`}
            onClick={() => navigate(`/escolher-turma?id=${acaoId}`)}
            disabled={!acaoId || !!msgError}
          >
            Li e concordo com as informações e o termo acima
          </Button>
        </div>
      </div>
    </>
  );
};

export default OrientacaoAcao;
